import { useAtom, useAtomValue } from 'jotai'
import { useEffect, useRef, useState } from 'react'

import { JobiLoader } from '../../../rapid-ui'
import { logger, PersistenceState } from '../../../utilities'
import { MediaAsset } from '../../../views'
import { BrandEditor, CornerRadius } from './brand-editor'
import { useBrandLibraryStore } from './brand-library-store'
import {
	expandedBrandingAtom,
	expandedBrandingIdAtom,
	expandedBrandingLogoCallbackAtom,
	expandedBrandingSavingAtom,
	expandedBrandingScanErrorAtom,
	expandedBrandingStatusAtom,
	expandedBrandingUrlCallbackAtom,
} from './state'

export type BrandLibraryDetailsProps = {
	onStateChange?: (state: PersistenceState) => void
}

export function BrandLibraryDetails(
	{ onStateChange }: BrandLibraryDetailsProps,
) {
	const store = useBrandLibraryStore()
	const [branding, setBranding] = useAtom(expandedBrandingAtom, { store })
	const [expandedBrandingId, setExpandedBrandingId] = useAtom(expandedBrandingIdAtom, { store })
	const [expandedBrandingStatus, setExpandedBrandingStatus] = useAtom(expandedBrandingStatusAtom, { store })
	const [expandedBrandingSaving, setExpandedBrandingSaving] = useAtom(expandedBrandingSavingAtom, { store })

	const onScanUrl = useAtomValue(expandedBrandingUrlCallbackAtom, { store })
	const scanError = useAtomValue(expandedBrandingScanErrorAtom, { store })

	const onLogoUpload = useAtomValue(expandedBrandingLogoCallbackAtom, { store })

	const [urlInput, setUrlInput] = useState('')
	const [nameInput, setNameInput] = useState('')
	const [cornerRadius, setCornerRadius] = useState<CornerRadius>('large')
	const nameInitializedForId = useRef<string | null>(null)

	useEffect(() => {
		// reset values
		// setExpandedBrandingStatus('pending')
		setExpandedBrandingSaving('idle')
		// setBranding(null)
		setUrlInput('')
		setNameInput('')
		nameInitializedForId.current = null
	}, [expandedBrandingId])

	// Initialize nameInput once the correct branding data has loaded
	useEffect(() => {
		if (branding && branding.id === expandedBrandingId && nameInitializedForId.current !== expandedBrandingId) {
			setNameInput(branding.name ?? '')
			nameInitializedForId.current = expandedBrandingId
		}
	}, [branding, expandedBrandingId])

	useEffect(() => {
		onStateChange?.(expandedBrandingSaving)
	}, [expandedBrandingSaving])

	const isGenerating = expandedBrandingStatus === 'generating'
	// Also treat as pending when branding data hasn't caught up to the selected ID yet.
	// expandedBrandingIdAtom updates immediately on click, but the data atom lags by one
	// render cycle (propagated via useEffect in useBrandLibrary), causing a stale flash.
	const isStale = !!expandedBrandingId && branding?.id !== expandedBrandingId
	const isPending = isStale || expandedBrandingStatus === 'pending' || expandedBrandingStatus === 'idle'

	const handleLogoUpload = (logo: File, callback: (asset: MediaAsset) => void, onFail?: () => void) => {
		logger.info('Starting logo upload', {
			category: 'branding',
			data: {
				branding_id: expandedBrandingId,
				logo_size: logo.size,
				logo_type: logo.type,
			},
		})
		onLogoUpload?.(logo, (media) => {
			if (!branding) {
				logger.error('Branding not found', {
					category: 'branding',
					data: {
						branding_id: expandedBrandingId,
					},
				})
				// this path shouldn't happen, but just tidy up the state in case.
				callback(media)
				return
			}
			setBranding({ ...branding, logoId: media.id, logo: media })
			callback(media)
		}, onFail)
	}

	if (!expandedBrandingId || !branding || isPending) {
		return (
			<div className="flex items-center justify-center">
				<JobiLoader />
			</div>
		)
	}

	return (
		<div className="max-w-[800px]">
			<BrandEditor
				branding={branding}
				onChange={(b) => setBranding(b)}
				isGenerating={isGenerating}
				isPending={isPending}
				nameValue={nameInput}
				onNameChange={(value) => {
					setNameInput(value)
					setBranding({ ...branding, name: value })
				}}
				urlValue={urlInput}
				onUrlChange={setUrlInput}
				onScanUrl={onScanUrl ?? undefined}
				scanError={scanError}
				cornerRadiusValue={cornerRadius}
				onCornerRadiusChange={(value) => setCornerRadius(value)}
				onLogoUpload={handleLogoUpload}
				showKeyImagePreference
			/>
		</div>
	)
}
